import React, { useState, useEffect } from "react";
import { updateClient } from "../api/clients";
import useCtx from "../hooks/useCts";
import { Plus, Trash2, Users, Copy } from "lucide-react";
import ModalConfirm from "./ModalConfirm";
import {
  ACTION_BUTTON_TEXT_CLASS_NAME,
  getActionButtonStyle,
} from "../utils/actionButtonStyles";

const Teachers: React.FC = () => {
  const ctx = useCtx();
  const client = ctx.clients.find((c) => c.authId === ctx.user?.uid);

  const [teachers, setTeachers] = useState<Client["teachers"]>([]);
  const [name, setName] = useState<string>("");
  const [surname, setSurname] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [copied, setCopied] = useState(false);
  const [teacherPendingDelete, setTeacherPendingDelete] = useState<{
    index: number;
    teacher: Teacher;
  } | null>(null);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(
    null,
  );

  useEffect(() => {
    if (client?.teachers) {
      setTeachers(client.teachers);
    }
  }, [client]);

  const handleAddTeacher = async () => {
    if (!client || !name || !surname || !email || !email.includes("@")) {
      alert("Wprowadź poprawne dane lektora.");
      return;
    }

    const newTeacher = {
      name,
      surname,
      email,
      permissions: [],
    };

    const updatedTeachers = [...(teachers || []), newTeacher];
    setTeachers(updatedTeachers);
    setName("");
    setSurname("");

    await updateClient({ id: client.id, teachers: updatedTeachers });
  };

  const handleRemoveTeacher = async (index: number) => {
    if (!client) return;

    const updatedTeachers = teachers?.filter((_, i) => i !== index);
    setTeachers(updatedTeachers);
    await updateClient({ id: client.id, teachers: updatedTeachers });
  };

  const handleConfirmRemoveTeacher = async () => {
    if (!teacherPendingDelete) return;

    try {
      setPendingDeleteIndex(teacherPendingDelete.index);
      await handleRemoveTeacher(teacherPendingDelete.index);
      setTeacherPendingDelete(null);
    } finally {
      setPendingDeleteIndex(null);
    }
  };

  if (!client) {
    return <p className="text-red-600">Nie znaleziono przypisanego klienta.</p>;
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 5000);
      })
      .catch((err) => {
        console.error("Błąd kopiowania: ", err);
      });
  };

  return (
    <div className="mx-auto my-2 max-w-4xl space-y-5">
      {/* ── Info banner ── */}
      <div className="rounded-[24px] border border-white/85 bg-white/5 p-5 shadow-[0_16px_45px_rgba(148,163,184,0.14)] backdrop-blur-md sm:p-6">
        <p className="text-xs mb-2 font-bold uppercase tracking-[0.18em] text-slate-600">
          Dodaj lub usuń lektorów
        </p>
        <p className="text-sm leading-7 text-slate-600">
          Lektorzy Twojej szkoły uzyskają dostęp wyłącznie do podręczników w
          wersji cyfrowej. Lektorzy <b>nie będą widzieć</b> zamówień, programu
          lojalnościowego ani strefy admina.
        </p>
      </div>

      {/* ── Add teacher form ── */}
      <div className="rounded-[24px] border border-white/85 bg-white/5 p-5 shadow-[0_16px_45px_rgba(148,163,184,0.14)] backdrop-blur-md sm:p-6">
        <div className="grid gap-3 sm:grid-cols-2">
          <input
            type="text"
            placeholder="Imię"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-white/85 px-4 py-3 text-slate-800 outline-none transition focus:border-orange-300 focus:ring-4 focus:ring-orange-100"
          />
          <input
            type="text"
            placeholder="Nazwisko"
            value={surname}
            onChange={(e) => setSurname(e.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-white/85 px-4 py-3 text-slate-800 outline-none transition focus:border-orange-300 focus:ring-4 focus:ring-orange-100"
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-white/85 px-4 py-3 text-slate-800 outline-none transition focus:border-orange-300 focus:ring-4 focus:ring-orange-100 sm:col-span-2"
          />

          <button
            onClick={handleAddTeacher}
            className={`mt-1 inline-flex items-center justify-center rounded-full px-6 py-3 ${ACTION_BUTTON_TEXT_CLASS_NAME} sm:col-span-2 sm:w-fit`}
            style={getActionButtonStyle("orange")}
          >
            Dodaj lektora
          </button>
        </div>
      </div>

      {/* ── Onboarding instructions ── */}
      <div className="rounded-[24px] border border-white/85 bg-white/5 p-5 shadow-[0_16px_45px_rgba(148,163,184,0.14)] backdrop-blur-md sm:p-6">
        <p className="mb-4 text-sm font-semibold text-slate-800">
          Co będzie potrzebne lektorom aby korzystać z podręczników w wersji
          cyfrowej?
        </p>

        <div className="space-y-2">
          {/* Link row */}
          <div className="flex items-center gap-3 rounded-2xl border border-slate-200/60 bg-white/60 px-4 py-3">
            <div className="min-w-0 flex-1 text-sm text-slate-700">
              <span className="text-slate-500">1. Link do systemu: </span>
              <b className="break-all">
                https://panel-klienta-direct-press.web.app/
              </b>
            </div>
            <button
              onClick={() =>
                copyToClipboard("https://panel-klienta-direct-press.web.app/")
              }
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${ACTION_BUTTON_TEXT_CLASS_NAME}`}
              style={getActionButtonStyle("slate")}
              title="Kopiuj link"
            >
              <Copy size={14} strokeWidth={2.2} />
            </button>
          </div>

          {/* Token row */}
          <div className="flex items-center gap-3 rounded-2xl border border-slate-200/60 bg-white/60 px-4 py-3">
            <div className="min-w-0 flex-1 text-sm text-slate-700">
              <span className="text-slate-500">2. Token dostępu: </span>
              <b>{client?.token}</b>
            </div>
            <button
              onClick={() => copyToClipboard(client?.token)}
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${ACTION_BUTTON_TEXT_CLASS_NAME}`}
              style={getActionButtonStyle("slate")}
              title="Kopiuj token"
            >
              <Copy size={14} strokeWidth={2.2} />
            </button>
          </div>
          <div className="flex items-center gap-3 rounded-2xl border border-slate-200/60 bg-white/60 px-4 py-3">
            <div className="min-w-0 flex-1 text-sm text-slate-700">
              <p className="mt-4 text-sm leading-7 text-slate-700">
                3. Przy pierwszej rejestracji należy podać email oraz ustawić
                silne hasło (conajmniej 1 duża litera, 1 cyfra, min. 8 znaków).
                Każdy kolejny raz to klasyczne logowanie (email, hasło) + token
                dostępu (ze względów bezpieczeństwa).
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Teacher list ── */}
      {teachers && teachers.length > 0 ? (
        <div className="rounded-[24px] border border-white/85 bg-white/5 shadow-[0_16px_45px_rgba(148,163,184,0.14)] backdrop-blur-md">
          <ul className="divide-y divide-slate-200/40">
            {teachers.map((t, i) => (
              <li
                key={i}
                className="flex items-center justify-between gap-4 px-5 py-4 sm:px-6"
              >
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800">
                    {t.name} {t.surname}
                  </p>
                  <p className="truncate text-sm text-slate-500">{t.email}</p>
                </div>

                <button
                  onClick={() => setTeacherPendingDelete({ index: i, teacher: t })}
                  className={`rounded-full px-4 py-3 ${ACTION_BUTTON_TEXT_CLASS_NAME}`}
                  style={getActionButtonStyle("red")}
                >
                  Usuń
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="rounded-[24px] border border-white/85 bg-white/5 p-5 text-sm text-slate-600 shadow-[0_16px_45px_rgba(148,163,184,0.14)] backdrop-blur-md sm:p-6">
          Nie dodano jeszcze żadnych lektorów.
        </div>
      )}

      {teacherPendingDelete && (
        <ModalConfirm
          message={`Czy na pewno chcesz usunąć lektora „${teacherPendingDelete.teacher.name} ${teacherPendingDelete.teacher.surname}”?`}
          onCancel={() => {
            if (pendingDeleteIndex === null) {
              setTeacherPendingDelete(null);
            }
          }}
          onConfirm={() => void handleConfirmRemoveTeacher()}
          cancelLabel="Anuluj"
          confirmLabel="Usuń lektora"
          isConfirming={pendingDeleteIndex === teacherPendingDelete.index}
          variant="bookmark"
          heading="Usuń lektora"
          icon={<Trash2 size={24} strokeWidth={2.1} aria-hidden="true" />}
        />
      )}
    </div>
  );
};

export default Teachers;
